import useLangText from '@shared/hooks/useLangText';
import {
    ServiceProcessButtonForServiceType,
    ServiceVmRaw,
} from '@shared/types/process';
import { resolveSignActionPreparation } from '@shared/util/signPreparation';

import { getHyperlinkOptions, HyperlinkOptions } from './serviceHyperlink';
import { ServiceReject } from './ServiceReject';

import { DynamicIcon, IconName } from 'lucide-react/dynamic';
import { ReactNode, useMemo } from 'react';

export type ServiceButtonAction = ServiceProcessButtonForServiceType & {
    actionCode: string;
    actionText: string;
    buttonType: string;
    isSign: boolean;
    signDocumentId: string | null;
    signButtonActionId: string | null;
    needsSignPreparation: boolean;
    serviceId?: string;
    hyperlinkOptions: HyperlinkOptions | null;
    type: string | null;
    delayedActionDueAt?: string;
    buttonId: string;
    buttonProps: {
        icon: ReactNode | undefined;
    };
};

function exceptionButton(buttons: ServiceProcessButtonForServiceType[]) {
    return buttons.filter((btn) => {
        if (btn.buttonActions?.some((action) => action.actionCode === 'Dialog'))
            return false;

        if (btn.buttonActions?.some((action) => action.actionCode === 'Reject'))
            return false;

        return true;
    });
}

export function useServiceBtnViewer({
    buttons,
    service,
}: {
    service?: ServiceVmRaw;
    buttons: ServiceProcessButtonForServiceType[];
}) {
    const getLangText = useLangText();

    const autoMakeBtns: ServiceButtonAction[] = useMemo(() => {
        if (!service) return [];
        const btns = exceptionButton(buttons);

        const actions = btns.map((x) => {
            const isSignAction = x.buttonActions?.some(
                (action) => action.actionCode === 'Sign',
            );
            const signPreparation = resolveSignActionPreparation(
                x.buttonActions,
            );

            let isSign = isSignAction ?? false;
            let signDocumentId: string | null = isSignAction
                ? (service?.signFileId ?? null)
                : null;
            let type: typeof x.buttonType | null = null;
            let signButtonActionId: string | null =
                signPreparation?.buttonActionId ?? null;
            let needsSignPreparation =
                Boolean(isSignAction) &&
                Boolean(signPreparation?.needsSignPreparation);

            switch (x.buttonType) {
                case 'Approve':
                    isSign = true;
                    signDocumentId =
                        service?.approvalInstances?.[0]?.originalFileId ?? null;
                    signButtonActionId = null;
                    needsSignPreparation = false;
                    break;
                case 'ApproveWithReason':
                    isSign = true;
                    signDocumentId =
                        service?.approvalInstances?.[0]?.originalFileId ?? null;
                    type = 'ApproveWithReason';
                    signButtonActionId = null;
                    needsSignPreparation = false;
                    break;
                case 'Reject':
                    isSign = false;
                    type = 'Reject';
                    signButtonActionId = null;
                    needsSignPreparation = false;
                    break;
                case 'Familiarize':
                    isSign = true;
                    signButtonActionId = null;
                    needsSignPreparation = false;
                    break;
                case 'AssignExecutor':
                    isSign = false;
                    type = 'AssignExecutor';
                    signButtonActionId = null;
                    needsSignPreparation = false;
                    break;
            }

            return {
                ...x,
                actionCode: x.id,
                actionText: getLangText(x.name),
                buttonType: x.buttonType,
                isSign,
                signDocumentId,
                signButtonActionId,
                needsSignPreparation,
                hyperlinkOptions: getHyperlinkOptions(x.buttonActions),
                type,
                buttonId: x.id,
                buttonProps: {
                    icon: x.icon && <DynamicIcon name={x.icon as IconName} />,
                },
            };
        });

        return actions;
    }, [buttons, getLangText, service]);

    const Reject = useMemo(() => {
        const btn = buttons.find((btn) =>
            btn?.buttonActions?.find(
                (action) => action.actionCode === 'Reject',
            ),
        );

        let component = null;
        if (service && service.rejectServiceProcessId) {
            component = ServiceReject;
        }
        return {
            Component: component,
            button: btn,
        };
    }, [buttons, service]);

    return {
        autoMakeBtns,
        Reject,
    };
}
